/**
 * What a refused token refresh means, shared by web.space and web.identity so
 * both read the backend's refusal codes from one list (FOX-6888). Pure and
 * dependency-free; each app unwraps its Apollo error shape before calling.
 */

/** True for the "user is not authorized" / `unauthorized.user` family. */
export function isUnauthorizedUserError(args: {
  code?: unknown;
  message?: unknown;
}): boolean {
  const code = typeof args.code === "string" ? args.code : "";
  const message = typeof args.message === "string" ? args.message : "";
  return (
    code === "unauthorized.user" ||
    code === "forbidden.invalid_access" ||
    message.includes("user is not authorized") ||
    message.includes("user can't access this")
  );
}

/**
 * Classify a token-refresh GraphQL failure by the backend error code/message.
 *
 *  - `invalid`   -> the refresh token itself is no good and re-auth is required:
 *                   the backend's `refreshAccessToken` throws
 *                   `bad_request.invalid_token` (revoked / not in DB / bad
 *                   signature) or `bad_request.missing_token`, plus the
 *                   unauthorized/forbidden family for defensiveness.
 *  - `transient` -> everything else: an `internal_service` 5xx (the FOX-3654
 *                   incident: OAuth-secret fetch failing), a network blip, a
 *                   timeout, or an unrecognised code.
 *
 * Fail-safe by design: only a POSITIVE invalid signal returns `invalid`; on any
 * ambiguity we return `transient` so a backend hiccup keeps the session (retry)
 * instead of logging the user out. Genuine invalid tokens always carry one of
 * the explicit codes above, so they are still caught.
 */
export function classifyRefreshErrorCodes(
  items: ReadonlyArray<
    { code?: unknown; message?: unknown } | null | undefined
  >,
): "invalid" | "transient" {
  for (const item of items) {
    if (!item) continue;
    const code = typeof item.code === "string" ? item.code : "";
    const message = typeof item.message === "string" ? item.message : "";
    if (
      code === "bad_request.invalid_token" ||
      code === "bad_request.missing_token" ||
      code.startsWith("unauthorized") ||
      code.startsWith("forbidden") ||
      // Message-text fallbacks for when the wrapper drops `extensions`.
      message.includes("token is invalid") ||
      message.includes("refresh token is missing") ||
      isUnauthorizedUserError(item)
    ) {
      return "invalid";
    }
  }
  return "transient";
}
